import { head, get, last, isNumber } from 'lodash';
import moment from 'moment';
import chapsSubmittedContent from '../../chaps/components/ChapsSubmittedPage/chapsSubmitted.content';

import {
	dateFormat,
	TRANSACTION_LIMIT_ERROR_CODE,
	TYPE_CHAPS,
	MISSED_DEADLINE_ERROR_CODE,
	PENDING_STATUS_ERROR_CODE,
} from '../../config';
import {
	getOneOffPaymentReviewRequest,
	getOneOffChapsPaymentConfirmationRequest,
	getChapsBatchPaymentConfirmationRequest,
	getBatchPaymentReviewRequest,
} from '../../helpers/apiRequestBuilder';
import {
	getBatchName,
	getBatchId,
	getBatchPaymentsStatus,
	getBatchAuthorisers,
	isBatch as getIsBatch,
	isUnauthorisedDailyLimit as batchExceedsDailyLimit,
	getBatchContextId,
	getBatchGroupAuthorisers,
} from '../batch/selectors';

import {
	getFromAccountById,
	getToAccountById,
	getAccountIsBillerOrCreditCard,
	isFutureDatedPaymentUnsupported,
	isSavingsAccount,
	getPaymentsWithAccounts,
} from '../accounts/selectors';
import { hasGroupApprovalsEnabled, isAuthorisationEdit, isPaymentInEditMode } from '../modes/selectors';
import {
	isBiller,
	unauthorisedDailyLimitFilter,
	toFormattedDate,
	toDistinctDates,
	fromPaymentToDateListItem,
} from '../commonSelectors/selectors';

/** @todo circular dependency */
import { getPaymentCategory } from '../payments/getPaymentCategory';

import ApiConstants from '../../config/apiConstants';
import chapsReviewContent from '../../chaps/components/ChapsReviewPage/chapsReview.content';
import { paymentReviewContent } from '../../sharedComponents/PaymentReview/PaymentReview.content';
import { getAuthorisationMessage, isUserPaymentAuthoriser } from '../../helpers/fieldHelpers';
import { removeDuplicateFromaccounts } from '../../helpers/apiRequestBuilder/common';

import { formatSortCode } from '../../utils/accountUtils';
import { getUserId } from '../user/selectors';

export const getChapsPaymentsList = (state) => get(state, 'chaps.payments', []);
export const getFirstPayment = (state) => head(getChapsPaymentsList(state));

export const getChapsPaymentsListToSubmitForReview = (state) => {
	const payments = getPaymentsWithAccounts(state, getChapsPaymentsList(state));
	const { bankId } = state.session;
	const isContextIdRequired = isAuthorisationEdit(state);
	const isEditMode = isPaymentInEditMode(state);

	return {
		batch_list: payments.map(getOneOffPaymentReviewRequest(bankId, isContextIdRequired, isEditMode, TYPE_CHAPS)),
	};
};

export const isUnauthorisedDailyLimit = (state) => getChapsPaymentsList(state).some(unauthorisedDailyLimitFilter);
export const getShowChapsDailyLimitModal = (state) => isUnauthorisedDailyLimit(state) || batchExceedsDailyLimit(state);

const fromChapsPaymentsToCumulativeAmountPerDate = (accumulatedAmounts, payment) => {
	const formattedDate = moment(payment.date).format(dateFormat);
	const accumulatedAmount = get(accumulatedAmounts, formattedDate, 0);
	const paymentAmount = parseFloat(get(payment, 'amount', '').replace(/,/g, ''));

	return {
		...accumulatedAmounts,
		[formattedDate]: accumulatedAmount + (Number.isNaN(paymentAmount) ? 0 : paymentAmount),
	};
};

const findFirstPaymentsWithCumulativeAmounts = (payments, amounts) =>
	Object.keys(amounts).map((paymentDate) => {
		const firstPayment = payments.find(({ date }) => moment(date).format(dateFormat) === paymentDate);
		const cumulativeAmount = amounts[paymentDate];
		return {
			...firstPayment,
			amount: cumulativeAmount.toLocaleString('en-GB', { minimumFractionDigits: 2 }),
		};
	});

export const getChapsPaymentsListToSubmitForDailyLimitReview = (state) => {
	const chapsPaymentsList = getChapsPaymentsList(state);
	const cumulativeAmounts = chapsPaymentsList.reduce(fromChapsPaymentsToCumulativeAmountPerDate, {});
	const firstPaymentsWithCumulativeAmounts = findFirstPaymentsWithCumulativeAmounts(
		chapsPaymentsList,
		cumulativeAmounts
	);
	const payments = getPaymentsWithAccounts(state, firstPaymentsWithCumulativeAmounts);
	const { bankId } = state.session;
	const isContextIdRequired = isAuthorisationEdit(state);
	const isEditMode = isPaymentInEditMode(state);

	return {
		batch_list: payments.map(getOneOffPaymentReviewRequest(bankId, isContextIdRequired, isEditMode, TYPE_CHAPS)),
	};
};

const getPaymentErrorMessage = (payment) => get(payment, 'confirmation.error.message');
const getPaymentMessage = (payment) => get(payment, 'confirmation.message');
const paymentIsObligate = (payment) => get(payment, 'authorisation.status', '') === ApiConstants.obligate;

export const getPaymentsTotal = (state) => {
	const payments = getChapsPaymentsList(state);
	const total = payments.reduce((accumulator, payment) => {
		const amount = parseFloat(get(payment, 'amount', '').replace(/,/g, ''));
		return accumulator + (Number.isNaN(amount) ? 0 : amount);
	}, 0);
	return total.toLocaleString('en-GB', { minimumFractionDigits: 2 });
};

export const getChapsFromAccount = (state) => {
	const payments = getChapsPaymentsList(state);
	const result = get(head(payments), 'fromAccount', '');

	return result;
};

export const getChapsFromAccounts = (state) => getChapsPaymentsList(state).map((payment) => payment.fromAccount);

const getFeeValue = (payment) => {
	const fee = get(payment, 'fees', false);
	return isNumber(fee) ? `£${fee.toFixed(2)}` : '-';
};

export const getChapsPaymentsForReview = (state) => {
	const payments = getChapsPaymentsList(state);
	const isBatch = getIsBatch(state);
	const batchTotal = getPaymentsTotal(state);
	const groupApprovalsEnabled = hasGroupApprovalsEnabled(state);
	const userId = getUserId(state);

	const data = payments.reduce((accumulator, payment) => {
		const fromAccount = getFromAccountById(state, payment.fromAccount);
		const toAccount = getToAccountById(state, payment.toAccount);
		const authorisers = get(payment, 'authorisers', []);
		const groupAuthorisers = get(payment, 'authorisersGroup', []);
		const hasAuthorisation = paymentIsObligate(payment) && !isBatch;
		const showChangeAuthorisers = hasAuthorisation && !!authorisers.length && !payment.isDefaultAuthorisers;
		const hasNoAvailableAuthorisers = hasAuthorisation && groupAuthorisers.length === 0;
		const { noAvailableAuthorisersErrorMessage } = paymentReviewContent;
		const feeValue = getFeeValue(payment);
		const content = get(payment, 'isTRAObligate', false)
			? chapsReviewContent.traObligateContent
			: chapsReviewContent.content;
		return [
			...accumulator,
			...[
				{
					rowId: payment.id,
					label: fromAccount.displayName,
					subLabel: `${formatSortCode(fromAccount.sortCode)} | ${fromAccount.accountNumber}`,
					accountHolder: fromAccount.metadata.account_name,
				},
				{
					rowId: payment.id,
					label: toAccount.displayName,
					subLabel: `${formatSortCode(toAccount.sortCode)} | ${toAccount.accountNumber}`,
				},
				{
					rowId: payment.id,
					label: `£${payment.amount}`,
				},
				{
					rowId: payment.id,
					label: payment.chapsReference || payment.reference,
				},
				{
					rowId: payment.id,
					label: payment.date ? moment(payment.date).format(dateFormat) : '',
				},
				{
					rowId: payment.id,
					label: 'CHAPS',
				},
				{
					rowId: payment.id,
					label: feeValue,
				},
				{
					...(groupApprovalsEnabled
						? {
								rowId: payment.id,
								paymentStatus: hasAuthorisation ? 'authorisation' : null,
								label: hasAuthorisation ? content.AuthorisationHeader : null,
								subLabel:
									hasAuthorisation && !hasNoAvailableAuthorisers
										? getAuthorisationMessage(authorisers, userId, content.AuthorisationByUserSubTitle)
										: null,
								errorLabel: hasNoAvailableAuthorisers ? noAvailableAuthorisersErrorMessage : null,
								viewAuthorisers: hasAuthorisation && !authorisers.length && !hasNoAvailableAuthorisers,
						  }
						: {
								rowId: payment.id,
								paymentStatus: hasAuthorisation ? 'authorisation' : null,
								label: hasAuthorisation ? content.AuthorisationHeader : null,
								subLabel:
									hasAuthorisation && !hasNoAvailableAuthorisers
										? getAuthorisationMessage(authorisers, userId, content.AuthorisationByUserSubTitle)
										: null,
								errorLabel: hasNoAvailableAuthorisers ? noAvailableAuthorisersErrorMessage : null,
								changeAuthorisers: showChangeAuthorisers,
								selectAuthoriser: hasAuthorisation && !authorisers.length && !hasNoAvailableAuthorisers,
						  }),
				},
			],
		];
	}, []);
	if (isBatch) {
		return [
			...data,
			...[
				{
					rowId: 'total',
					label: 'TOTAL',
				},
				{
					rowId: 'total',
				},

				{
					rowId: 'total',
					label: `£${batchTotal}`,
				},
				{
					rowId: 'total',
				},
				{
					rowId: 'total',
				},
				{
					rowId: 'total',
				},
				{
					rowId: 'total',
				},
				{
					rowId: 'total',
				},
			],
		];
	}
	return data;
};

const isChapsPaymentMissedDeadline = (payment) =>
	get(payment, 'confirmation.error.code', '') === MISSED_DEADLINE_ERROR_CODE;

const getSubLabel = (state, statusSubLabel, paymentStatus, payment) => {
	if (!paymentStatus) return null;
	if (statusSubLabel) return statusSubLabel;
	let messageFromPayment = getPaymentMessage(payment);

	if (paymentStatus === 'authorisation') {
		const userId = getUserId(state);
		const authorisers = get(payment, 'authorisers', []);
		if (!isUserPaymentAuthoriser(authorisers, userId) || authorisers.length > 1) {
			return getAuthorisationMessage(authorisers, userId);
		}
		const authType = get(payment, 'confirmation.authType');

		if (authType === 'HARD') return 'HardTokenContent';
		return 'SoftTokenContent';
	}

	if (paymentStatus === 'failure') {
		if (isChapsPaymentMissedDeadline(payment)) return chapsSubmittedContent.submissionMissedChapsDeadlineErrorText;
		messageFromPayment = getPaymentErrorMessage(payment);
		if (!messageFromPayment) {
			return chapsSubmittedContent.submissionErrorText;
		}
	}

	return messageFromPayment;
};

const hardTokenFilter = ({ confirmation: { authType = '' } = {} }) => authType === ApiConstants.hardToken;
export const isHardTokenAuthorisation = (state) => getChapsPaymentsList(state).filter(hardTokenFilter).length > 0;

const getIndividualPaymentStatus = (batchPaymentsStatusList, paymentId) =>
	batchPaymentsStatusList.find(({ id }) => id === paymentId) || batchPaymentsStatusList[0];

const getIndividualBatchPaymentStatus = (status) => {
	switch (status.toUpperCase()) {
		case ApiConstants.obligate:
			return null;
		case ApiConstants.failed:
			return 'failure';
		case ApiConstants.success:
			return 'success';
		default:
			return status;
	}
};

const getStatusTitle = (statusTitle) => {
	switch (statusTitle.toUpperCase()) {
		case ApiConstants.success:
			return chapsSubmittedContent.paymentSubmittedHeader;
		case ApiConstants.obligate:
			return null;
		case ApiConstants.failed:
			return chapsSubmittedContent.submissionErrorHeader;
		default:
			return statusTitle;
	}
};

const isPendingStatus = (payment, code) =>
	(get(payment, 'confirmation.error.code', 0) || code) === PENDING_STATUS_ERROR_CODE;
export const getLabel = ({ payment, statusTitle, status, code }) => {
	if (isPendingStatus(payment, code)) return chapsSubmittedContent.submissionOnHoldHeader;
	if (statusTitle) return statusTitle;
	return getStatusTitle(status);
};

const reduceChapsPayments = (state, payments, statusTitle, paymentStatus) =>
	payments.reduce((accumulator, payment) => {
		const fromAccount = getFromAccountById(state, payment.fromAccount);
		const toAccount = getToAccountById(state, payment.toAccount);
		const individualPaymentStatus = getIndividualPaymentStatus(getBatchPaymentsStatus(state), payment.id);
		const statusSubLabel = get(individualPaymentStatus, 'statusMessage', null);
		const status = get(individualPaymentStatus, 'status', '');
		const code = get(individualPaymentStatus, 'code', '');
		const calculatedPaymentStatus = paymentStatus || getIndividualBatchPaymentStatus(status);
		const feeValue = getFeeValue(payment);

		return [
			...accumulator,
			...[
				{
					rowId: payment.id,
					label: fromAccount.displayName,
					subLabel: `${formatSortCode(fromAccount.sortCode)} | ${fromAccount.accountNumber}`,
					accountHolder: fromAccount.metadata.account_name,
				},
				{
					rowId: payment.id,
					label: toAccount.displayName,
					subLabel: `${formatSortCode(toAccount.sortCode)} | ${toAccount.accountNumber}`,
				},
				{
					rowId: payment.id,
					label: `£${payment.amount}`,
				},
				{
					rowId: payment.id,
					label: payment.reference,
				},
				{
					rowId: payment.id,
					label: payment.date ? moment(payment.date).format(dateFormat) : '',
				},
				{
					rowId: payment.id,
					label: 'CHAPS',
				},
				{
					rowId: payment.id,
					label: feeValue,
				},
				{
					rowId: payment.id,
					paymentStatus: calculatedPaymentStatus,
					label: getLabel({ payment, statusTitle, status, code }),
					subLabel: getSubLabel(state, statusSubLabel, calculatedPaymentStatus, payment),
				},
			],
		];
	}, []);

const confirmedFilter = ({ confirmation: { responseCode = '' } = {} }) =>
	responseCode === 200 || responseCode === 422 || responseCode === 202;

export const getChapsSubmittedPaymentsList = (state) => {
	const payments = getChapsPaymentsList(state).filter(confirmedFilter);
	const statusTitle = chapsSubmittedContent.paymentSubmittedHeader;
	const isBatch = getIsBatch(state);

	if (isBatch) {
		return getChapsPaymentsForReview(state);
	}
	return reduceChapsPayments(state, payments, statusTitle, 'success');
};

export const getChapsBatchSubmittedPaymentsList = (state) => {
	const data = reduceChapsPayments(state, getChapsPaymentsList(state));
	const batchTotal = getPaymentsTotal(state);

	return [
		...data,
		{
			rowId: 'total',
			label: 'TOTAL',
		},
		{
			rowId: 'total',
		},

		{
			rowId: 'total',
			label: `£${batchTotal}`,
		},
		{
			rowId: 'total',
		},
		{
			rowId: 'total',
		},
		{
			rowId: 'total',
		},
		{
			rowId: 'total',
		},
		{
			rowId: 'total',
		},
	];
};

const awaitingAuthorisationFilter = ({ confirmation: { responseCode = '' } = {} }) => responseCode === 401;

export const getChapsPaymentsAwaitingAuthorisation = (state) => {
	const payments = getChapsPaymentsList(state).filter(awaitingAuthorisationFilter);
	const statusTitle = chapsSubmittedContent.authorisationRequired;
	return reduceChapsPayments(state, payments, statusTitle, 'authorisation');
};

const failedFilter = ({ confirmation: { responseCode = '', error: { code = '' } = {} } = {} }) =>
	[500, 409, 400, 404].includes(responseCode) && code !== PENDING_STATUS_ERROR_CODE;
const pendingFilter = ({ confirmation: { responseCode = '', error: { code = '' } = {} } = {} }) =>
	responseCode === 409 && code === PENDING_STATUS_ERROR_CODE;

export const getChapsFailedPaymentsList = (state) => {
	const payments = getChapsPaymentsList(state).filter(failedFilter);
	const statusTitle = chapsSubmittedContent.submissionErrorHeader;
	return reduceChapsPayments(state, payments, statusTitle, 'failure');
};

export const getChapsPendingPaymentsList = (state) => {
	const payments = getChapsPaymentsList(state).filter(pendingFilter);
	const statusTitle = chapsSubmittedContent.submissionOnHoldHeader;
	return reduceChapsPayments(state, payments, statusTitle, 'failure');
};

export const canApplySameForAll = (state) => {
	const payments = getChapsPaymentsList(state);
	return Boolean(payments.length > 1 && get(payments, '1.toAccount'));
};

export const getPaymentIdsforSameReference = (state) =>
	getChapsPaymentsList(state)
		.filter((payment) => !getAccountIsBillerOrCreditCard(state, payment.toAccount))
		.map((payment) => payment.id);

export const getPaymentIdsforSameDate = (state) =>
	getChapsPaymentsList(state)
		.filter((payment) => !(isBiller(state, payment.toAccount) || isSavingsAccount(state, payment.fromAccount)))
		.map((payment) => payment.id);

const getConfirmedChapsBatchToSubmit = (state) => {
	const payments = getPaymentsWithAccounts(state, getChapsPaymentsList(state));
	const { bankId } = state.session;
	const batchId = getBatchId(state);
	const batchName = getBatchName(state);
	const authorisers = getBatchAuthorisers(state);
	const groupAuthorisers = getBatchGroupAuthorisers(state);

	return {
		batch_list: payments.map((payment) =>
			getChapsBatchPaymentConfirmationRequest(bankId, payment, batchName, batchId, authorisers, groupAuthorisers)
		),
	};
};

const getConfirmedChapsPaymentsListToSubmit = (state) => {
	const payments = getPaymentsWithAccounts(state, getChapsPaymentsList(state));
	const { bankId } = state.session;

	return {
		batch_list: payments.map((payment) => getOneOffChapsPaymentConfirmationRequest(bankId, payment)),
	};
};

export const getSubmitConfirmedChapsPayments = (state) => {
	if (getIsBatch(state)) return getConfirmedChapsBatchToSubmit(state);
	return getConfirmedChapsPaymentsListToSubmit(state);
};
export const isMultiplePayments = (state) => getChapsPaymentsList(state).length > 1;

export const getChapsSingleAuthorisersList = (state, paymentId) =>
	get(
		getChapsPaymentsList(state).find((payment) => payment.id === paymentId),
		'singleAuthoriserList',
		[]
	);

export const getChapsMultiAuthorisersList = (state, paymentId) =>
	get(
		getChapsPaymentsList(state).find((payment) => payment.id === paymentId),
		'multiAuthoriserList',
		[]
	);

export const getChapsPaymentsRequireAuthorisers = (state) => {
	const payments = getChapsPaymentsList(state);
	return payments.some((payment) => {
		const authorisersRequired = get(payment, 'authorisation.status', '') === ApiConstants.obligate;
		const hasAuthorisers = hasGroupApprovalsEnabled(state)
			? get(payment, 'authorisersGroup', []).length > 0
			: get(payment, 'authorisers', []).length > 0;
		return authorisersRequired && !hasAuthorisers;
	});
};

export const isPaymentsRequireAuthorisation = (state) => getChapsPaymentsList(state).some(paymentIsObligate);

export const isBatchEligible = (state) => {
	const payments = getChapsPaymentsList(state);
	if (!(payments.length > 1)) return false;
	const firstPayment = head(payments);
	if (payments.some((payment) => !moment(payment.date).startOf('day').isSame(firstPayment.date, 'day'))) {
		return false;
	}
	if (payments.some((payment) => payment.fromAccount !== firstPayment.fromAccount)) return false;

	return true;
};

export const getChapsBatchToSubmitForReview = (state) => {
	const payments = getPaymentsWithAccounts(state, getChapsPaymentsList(state));
	const paymentCategory = getPaymentCategory(state);
	const { bankId } = state.session;
	const batchId = getBatchId(state);
	const payment = head(payments);
	const total = parseFloat(getPaymentsTotal(state).replace(/,/g, ''));
	const batchContextId = getBatchContextId(state);
	const isContextIdRequired = isAuthorisationEdit(state);
	const isEditMode = isPaymentInEditMode(state);

	const filteredPayments = removeDuplicateFromaccounts(payments);

	return {
		batch_list: [
			getBatchPaymentReviewRequest({
				bankId,
				batchId,
				total,
				payment_category: paymentCategory,
				payment,
				filteredPayments,
				isContextIdRequired,
				batchContextId,
				isEditMode,
			}),
		],
	};
};

export const showAddNewPaymentButton = () => true;

export const getIsEditing = (state) => get(state, 'chaps.editing', false);

const paymentDoesNotHaveFromOrToAccount = (element) =>
	get(element, 'toAccount', '') === '' || get(element, 'fromAccount', '') === '';

export const getPaymentsHaveFromAndToAccount = (state) =>
	!getChapsPaymentsList(state).some(paymentDoesNotHaveFromOrToAccount);

export const getAnyPaymentToBillerOrCreditCard = (state) => {
	const payments = getChapsPaymentsList(state);
	return payments.some((payment) => getAccountIsBillerOrCreditCard(state, payment.toAccount));
};

export const isAnyFutureDatedPaymentUnsupported = (state) => {
	const payments = getChapsPaymentsList(state);
	return payments.some((payment) => isFutureDatedPaymentUnsupported(state, payment.fromAccount, payment.toAccount));
};

export const isFutureDatedPayment = (payment) => (moment.isMoment(payment.date) ? payment.date.isAfter() : false);

export const getFirstPaymentIsFutureDated = (state) => {
	const payment = head(get(state, 'chaps.payments', []));
	return payment && isFutureDatedPayment(payment);
};

const isTransactionLimitError = (authorisation) => get(authorisation, 'error.code', 0) === TRANSACTION_LIMIT_ERROR_CODE;
const isConflict = (authorisation) => get(authorisation, 'responseCode', 0) === 409;

export const hasTransactionLimitError = (authorisation) =>
	isConflict(authorisation) && isTransactionLimitError(authorisation);

export const getChapsDeadlineMissed = (state) => get(state.chaps, 'deadlineMissed', false);

export const getChapsUnauthorisedDailyLimitDateList = (state) =>
	getChapsPaymentsList(state)
		.filter(unauthorisedDailyLimitFilter)
		.map(toFormattedDate)
		.reduce(toDistinctDates, [])
		.map(fromPaymentToDateListItem);

export const getChapsDistinctPaymentDateList = (state) =>
	getChapsPaymentsList(state).map(toFormattedDate).reduce(toDistinctDates, []).map(fromPaymentToDateListItem);

export const getChapsDailyLimitModalDates = (state) => {
	if (getIsBatch(state)) {
		return getChapsDistinctPaymentDateList(state);
	}
	return getChapsUnauthorisedDailyLimitDateList(state);
};

export const getLastChapsPayment = (state) => last(getChapsPaymentsList(state));

export const getChapsPaymentCount = (state) => getChapsPaymentsList(state).length;

export const getToAccount = (state) => {
	const selectedPaymentId = get(state, 'payeeSelector.selectedPaymentId', '');
	if (selectedPaymentId) {
		const payment = getChapsPaymentsList(state).find(({ id }) => id === selectedPaymentId);
		return get(payment, 'toAccount', '');
	}
	return '';
};

export const getChapsReference = (state, id) => {
	const chapsPayments = get(state, 'chaps.payments', []);
	const chapsPayment = chapsPayments.find((payment) => payment.id === id);
	return get(chapsPayment, 'chapsReference');
};

export const getSingleChapsPaymentIdByToAccount = (state, toAccount) => {
	const paymentsList = getChapsPaymentsList(state);
	const payment = paymentsList.find((payment) => get(payment, 'toAccount') === toAccount);
	return get(payment, 'id', '');
};
